import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import { useQuery } from '@apollo/client'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import { onReschedule, onPayment, onCancellation } from 'lib/utils'
import { RefetchContext } from 'lib/utils/refetchContext'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { GET_COACH_AVAILABILITY_OVERRIDES } from 'pages/pga-coach/settings/availability/custom-availability/queries'
import BOOKINGS_QUERY from '../bookings-query'
import { bookingsInDateRange } from '../bookings-list-helpers'
import { SCHEDULE_BOOKINGS_REFETCH_EVENT } from '../schedule-bookings-refetch'
import CalendarToolbar from './calendar-toolbar'
import CalendarTimeGrid from './calendar-time-grid'
import CalendarMonthView from './calendar-month-view'
import CalendarListView from './calendar-list-view'
import CalendarMobileAgendaView from './calendar-mobile-agenda-view'
import {
  GET_SCHEDULE_AVAILABILITY,
  GET_SCHEDULE_BLOCK_TIMES,
} from './schedule-availability-queries'
import {
  buildAvailabilityByDate,
  buildBlockedBookingsForDays,
  mergeBookingsWithBlockedTimes,
} from './schedule-availability-layers'
import {
  getScheduleEventType,
  SCHEDULE_EVENT_TYPES,
} from 'pages/pga-coach/dashboard/todays-schedule/schedule-event-colors'
import {
  CALENDAR_VIEWS,
  computeVisibleHourRange,
  DESKTOP_CALENDAR_MIN_WIDTH,
  getBookingDateTime,
  getCalendarDateRange,
  getCalendarRangeLabel,
  getMobileCalendarView,
  getMonthGridDays,
  getWeekDays,
  groupBookingsByDate,
} from './calendar-utils'
import ScheduleAddToCalendarDialog from '../schedule-add-to-calendar-dialog'
import useCalendarStyles from './calendar-styles'
import {
  trackScheduleAddToCalendarOpened,
  trackScheduleBookingsOnlyFilterChanged,
  trackScheduleCalendarViewChanged,
  trackScheduleMonthDaySelected,
} from './schedule-calendar-analytics'

const SCHEDULE_ORDER_BY = [{ field: 'START_DATE_TIME', direction: 'ASC' }]

const ScheduleCalendar = ({
  onMetadataChange,
  canBookStudent = false,
  bookingUrl,
}) => {
  const classes = useCalendarStyles()
  const theme = useTheme()
  const calendarCardRef = useRef(null)
  const [calendarWidth, setCalendarWidth] = useState(null)
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'))
  const isMobile =
    calendarWidth != null && calendarWidth > 0
      ? calendarWidth < DESKTOP_CALENDAR_MIN_WIDTH
      : isMobileViewport
  const [view, setView] = useState(CALENDAR_VIEWS.WEEK)
  const mobileView = getMobileCalendarView(view)
  const [anchorDate, setAnchorDate] = useState(() => DateTime.local())
  const [selectedFacility, setSelectedFacility] = useState('all')
  const [bookingsOnly, setBookingsOnly] = useState(false)
  const [addToCalendarState, setAddToCalendarState] = useState({
    open: false,
    day: null,
    hour: null,
  })

  const { start, end } = useMemo(() => {
    if (isMobile) {
      return getCalendarDateRange(mobileView, anchorDate)
    }

    return getCalendarDateRange(view, anchorDate)
  }, [isMobile, mobileView, view, anchorDate])

  const [isCoachNotesEnabled] = useFeatureFlags([flags.FLAG_FEAT_COACH_NOTES])

  // Pad the GraphQL window by a day so facility-timezone midnight events near
  // week/day boundaries are still returned when the browser zone differs (e.g.
  // Denver local week start vs Chicago 12:00 AM Sunday). Client filtering uses
  // the booking timezone for the visible range.
  const queryVariables = useMemo(
    () => ({
      startDateTime: {
        from: start.minus({ days: 1 }).toISO(),
        to: end.plus({ days: 1 }).toISO(),
      },
      orderBy: SCHEDULE_ORDER_BY,
      ...(isCoachNotesEnabled ? { includeCoachNotes: true } : {}),
    }),
    [start, end, isCoachNotesEnabled],
  )

  const { data, loading, error, refetch } = useQuery(BOOKINGS_QUERY, {
    variables: queryVariables,
  })

  useEffect(() => {
    const onRefetch = () => refetch()
    window.addEventListener(SCHEDULE_BOOKINGS_REFETCH_EVENT, onRefetch)
    return () =>
      window.removeEventListener(SCHEDULE_BOOKINGS_REFETCH_EVENT, onRefetch)
  }, [refetch])

  const schedule = useMemo(() => data?.currentUser?.schedule || [], [
    data?.currentUser?.schedule,
  ])
  const filteredSchedule = useMemo(() => {
    const visibleBookings = schedule.filter(booking => !booking.isCanceled)

    if (
      selectedFacility === 'all' ||
      selectedFacility?.id === 'all' ||
      !selectedFacility
    ) {
      return visibleBookings
    }

    const facilityId =
      typeof selectedFacility === 'string'
        ? selectedFacility
        : selectedFacility.id

    return visibleBookings.filter(booking => {
      if (getScheduleEventType(booking) === SCHEDULE_EVENT_TYPES.BLOCKED_TIME) {
        const bookingFacilityId = booking.coachFacility?.id
        if (!bookingFacilityId) return true
        return bookingFacilityId === facilityId
      }
      return booking.coachFacility?.id === facilityId
    })
  }, [schedule, selectedFacility])

  const bookings = useMemo(
    () => bookingsInDateRange(filteredSchedule, start, end),
    [filteredSchedule, start, end],
  )

  const weekDays = useMemo(() => getWeekDays(anchorDate), [anchorDate])
  const calendarDays = useMemo(() => {
    if (isMobile) {
      return mobileView === CALENDAR_VIEWS.LIST
        ? weekDays
        : getMonthGridDays(anchorDate)
    }

    if (view === CALENDAR_VIEWS.DAY) return [anchorDate]
    if (view === CALENDAR_VIEWS.WEEK || view === CALENDAR_VIEWS.LIST) {
      return weekDays
    }

    return getMonthGridDays(anchorDate)
  }, [anchorDate, isMobile, mobileView, view, weekDays])

  const availabilityDays = useMemo(() => {
    if (isMobile) return []
    if (view === CALENDAR_VIEWS.DAY) return [anchorDate]
    if (view === CALENDAR_VIEWS.WEEK) return weekDays
    return []
  }, [isMobile, view, anchorDate, weekDays])

  const shouldLoadAvailability = availabilityDays.length > 0 && !bookingsOnly
  const shouldLoadBlockTimes = !bookingsOnly
  const canOpenAddToCalendar =
    canBookStudent && Boolean(bookingUrl) && !isMobile
  const shouldLoadOverrides =
    canOpenAddToCalendar &&
    (availabilityDays.length > 0 || view === CALENDAR_VIEWS.MONTH)
  const showBookingsOnlyFilter = true

  const {
    data: availabilityData,
    loading: availabilityLoading,
    refetch: refetchAvailability,
  } = useQuery(GET_SCHEDULE_AVAILABILITY, {
    skip: !shouldLoadAvailability,
  })
  const {
    data: overridesData,
    loading: overridesLoading,
    refetch: refetchOverrides,
  } = useQuery(GET_COACH_AVAILABILITY_OVERRIDES, {
    skip: !shouldLoadOverrides,
    variables: {
      dateRange: { from: start.toISODate() },
    },
  })
  const {
    data: blocksData,
    loading: blocksLoading,
    refetch: refetchBlockTimes,
  } = useQuery(GET_SCHEDULE_BLOCK_TIMES, {
    skip: !shouldLoadBlockTimes,
    variables: {
      datetimeRange: {
        from: start.minus({ days: 1 }).toISO(),
        to: end.plus({ days: 1 }).toISO(),
      },
    },
  })

  const availabilityQueriesLoading =
    shouldLoadAvailability &&
    (availabilityLoading || overridesLoading || blocksLoading)

  const availabilityByDate = useMemo(() => {
    if (!shouldLoadAvailability || availabilityQueriesLoading) {
      return null
    }

    return buildAvailabilityByDate({
      days: availabilityDays,
      availabilities: availabilityData?.coachAvailabilities || [],
      overrides: overridesData?.coachAvailabilityOverrides || [],
      blocks: blocksData?.coachAvailabilityBlockTimes || [],
      selectedFacility,
    })
  }, [
    availabilityDays,
    availabilityData,
    availabilityQueriesLoading,
    blocksData,
    overridesData,
    selectedFacility,
    shouldLoadAvailability,
  ])

  const showAvailabilityOverlay =
    shouldLoadAvailability && !availabilityQueriesLoading

  const blockedBookings = useMemo(() => {
    if (!shouldLoadBlockTimes || blocksLoading) {
      return []
    }

    return buildBlockedBookingsForDays({
      days: calendarDays,
      blocks: blocksData?.coachAvailabilityBlockTimes || [],
      availabilities: availabilityData?.coachAvailabilities || [],
      selectedFacility,
    })
  }, [
    availabilityData,
    blocksData,
    blocksLoading,
    calendarDays,
    selectedFacility,
    shouldLoadBlockTimes,
  ])

  const visibleBookings = useMemo(() => {
    const merged = shouldLoadBlockTimes
      ? mergeBookingsWithBlockedTimes(bookings, blockedBookings)
      : bookings

    return [...merged].sort((left, right) => {
      const leftStart = getBookingDateTime(left)
      const rightStart = getBookingDateTime(right)

      if (!leftStart.isValid) return 1
      if (!rightStart.isValid) return -1

      return leftStart.toMillis() - rightStart.toMillis()
    })
  }, [blockedBookings, bookings, shouldLoadBlockTimes])

  const bookingsByDate = useMemo(() => groupBookingsByDate(visibleBookings), [
    visibleBookings,
  ])

  const coachFacilities = useMemo(() => {
    const accountFacilities =
      data?.currentUser?.attributes?.facilities?.filter(
        facility => facility?.id && facility?.name,
      ) || []

    if (accountFacilities.length > 0) {
      return accountFacilities
    }

    // Fallback: facilities referenced by bookings in the current range.
    const facilities = schedule
      .map(booking => booking.coachFacility)
      .filter(facility => facility?.id && facility?.name)

    return facilities.filter(
      (facility, index, all) =>
        all.findIndex(item => item.id === facility.id) === index,
    )
  }, [data?.currentUser?.attributes?.facilities, schedule])

  const hasOneLocation = coachFacilities.length <= 1
  const defaultFacilityId = useMemo(() => {
    if (
      !selectedFacility ||
      selectedFacility === 'all' ||
      selectedFacility?.id === 'all'
    ) {
      return undefined
    }

    return typeof selectedFacility === 'string'
      ? selectedFacility
      : selectedFacility.id
  }, [selectedFacility])
  const { startHour, endHour } = useMemo(
    () => computeVisibleHourRange(visibleBookings),
    [visibleBookings],
  )

  const rangeLabel = useMemo(() => {
    if (isMobile) {
      return mobileView === CALENDAR_VIEWS.LIST
        ? getCalendarRangeLabel(CALENDAR_VIEWS.LIST, anchorDate)
        : anchorDate.toFormat('LLLL yyyy')
    }

    return getCalendarRangeLabel(view, anchorDate)
  }, [isMobile, mobileView, view, anchorDate])

  useEffect(() => {
    if (onMetadataChange) {
      onMetadataChange({ rangeLabel })
    }
  }, [onMetadataChange, rangeLabel])

  useEffect(() => {
    const node = calendarCardRef.current
    if (!node) {
      return undefined
    }

    const updateWidth = () => {
      setCalendarWidth(node.getBoundingClientRect().width)
    }

    updateWidth()

    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(updateWidth)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const handleViewChange = useCallback(
    nextView => {
      trackScheduleCalendarViewChanged(view, nextView)
      setView(nextView)
    },
    [view],
  )

  const handleBookingsOnlyChange = useCallback(nextBookingsOnly => {
    trackScheduleBookingsOnlyFilterChanged(nextBookingsOnly)
    setBookingsOnly(nextBookingsOnly)
  }, [])

  const handleBookSlotClick = useCallback(
    (event, slotContext) => {
      event?.preventDefault?.()
      trackScheduleAddToCalendarOpened({
        calendarView: view,
        day: slotContext.day,
        hour: slotContext.hour,
      })
      setAddToCalendarState({
        open: true,
        day: slotContext.day,
        hour: slotContext.hour,
      })
    },
    [view],
  )

  const handleMonthDayClick = useCallback(day => {
    trackScheduleMonthDaySelected(day)
    setAnchorDate(day)
    setView(CALENDAR_VIEWS.DAY)
  }, [])

  const handleCloseAddToCalendar = useCallback(() => {
    setAddToCalendarState({ open: false, day: null, hour: null })
  }, [])

  const handleAvailabilityChange = useCallback(() => {
    refetchAvailability()
    refetchOverrides()
    refetchBlockTimes()
    refetch()
  }, [refetch, refetchAvailability, refetchBlockTimes, refetchOverrides])

  const handleReschedule = useCallback(() => onReschedule(refetch), [refetch])
  const handlePayment = useCallback(() => onPayment(refetch), [refetch])
  const handleCancellation = useCallback(() => onCancellation(refetch), [
    refetch,
  ])

  const sessionRowHandlers = {
    onReschedule: handleReschedule,
    onPayment: handlePayment,
    onCancellation: handleCancellation,
  }

  const renderView = () => {
    if (loading) {
      return <LinearProgress color="primary" />
    }

    if (error) {
      return (
        <Box p={3}>
          <Typography color="error">
            We couldn&apos;t load your schedule. Please refresh and try again.
          </Typography>
        </Box>
      )
    }

    if (isMobile) {
      if (mobileView === CALENDAR_VIEWS.LIST) {
        return (
          <CalendarListView
            days={weekDays}
            bookingsByDate={bookingsByDate}
            {...sessionRowHandlers}
          />
        )
      }

      return (
        <CalendarMobileAgendaView
          anchorDate={anchorDate}
          onDateChange={setAnchorDate}
          bookingsByDate={bookingsByDate}
          {...sessionRowHandlers}
        />
      )
    }

    if (view === CALENDAR_VIEWS.DAY) {
      return (
        <CalendarTimeGrid
          days={[anchorDate]}
          bookings={visibleBookings}
          startHour={startHour}
          endHour={endHour}
          title={anchorDate.toFormat('cccc, LLL d')}
          canBookStudent={canBookStudent}
          bookingUrl={bookingUrl}
          onBookSlotClick={handleBookSlotClick}
          availabilityByDate={
            showAvailabilityOverlay ? availabilityByDate : null
          }
          showAvailabilityLegend={showAvailabilityOverlay}
          {...sessionRowHandlers}
        />
      )
    }

    if (view === CALENDAR_VIEWS.WEEK) {
      return (
        <CalendarTimeGrid
          days={weekDays}
          bookings={visibleBookings}
          startHour={startHour}
          endHour={endHour}
          canBookStudent={canBookStudent}
          bookingUrl={bookingUrl}
          onBookSlotClick={handleBookSlotClick}
          availabilityByDate={
            showAvailabilityOverlay ? availabilityByDate : null
          }
          showAvailabilityLegend={showAvailabilityOverlay}
          {...sessionRowHandlers}
        />
      )
    }

    if (view === CALENDAR_VIEWS.MONTH) {
      return (
        <CalendarMonthView
          anchorDate={anchorDate}
          bookingsByDate={bookingsByDate}
          canBookStudent={canBookStudent}
          bookingUrl={bookingUrl}
          onDayClick={handleMonthDayClick}
          onBookSlotClick={handleBookSlotClick}
          {...sessionRowHandlers}
        />
      )
    }

    return (
      <CalendarListView
        days={weekDays}
        bookingsByDate={bookingsByDate}
        {...sessionRowHandlers}
      />
    )
  }

  return (
    <RefetchContext.Provider value={refetch}>
      <Box className={classes.root}>
        <Box className={classes.calendarCard} ref={calendarCardRef}>
          <CalendarToolbar
            view={view}
            anchorDate={anchorDate}
            isMobile={isMobile}
            onViewChange={handleViewChange}
            onToday={() => setAnchorDate(DateTime.local())}
            onDateChange={setAnchorDate}
            selectedFacility={selectedFacility}
            onSelectedFacilityChange={setSelectedFacility}
            coachFacilities={coachFacilities}
            hasOneLocation={hasOneLocation}
            showBookingsOnlyFilter={showBookingsOnlyFilter}
            bookingsOnly={bookingsOnly}
            onBookingsOnlyChange={handleBookingsOnlyChange}
          />

          {renderView()}
        </Box>
        <ScheduleAddToCalendarDialog
          open={addToCalendarState.open}
          onClose={handleCloseAddToCalendar}
          slotContext={{
            day: addToCalendarState.day,
            hour: addToCalendarState.hour,
          }}
          bookingUrl={bookingUrl}
          facilities={coachFacilities}
          defaultFacilityId={defaultFacilityId}
          existingOverrides={
            shouldLoadOverrides
              ? overridesLoading
                ? undefined
                : overridesData?.coachAvailabilityOverrides ?? []
              : undefined
          }
          onAvailabilityChange={handleAvailabilityChange}
        />
      </Box>
    </RefetchContext.Provider>
  )
}

ScheduleCalendar.propTypes = {
  onMetadataChange: PropTypes.func,
  canBookStudent: PropTypes.bool,
  bookingUrl: PropTypes.string,
}

export default ScheduleCalendar
